import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { usePrefs } from '../lib/prefs.js';
import { useT } from '../lib/i18n.js';
import { TIERS, TIER_COLOR } from '../lib/tiers.js';
import type { Memory } from '../lib/memoryModel.js';
import { MemoryEditor } from '../components/MemoryEditor.js';
import { MemoryCard } from '../components/MemoryCard.js';
import { DedupModal, type DupGroup } from '../components/DedupModal.js';
import { DocumentsView } from '../components/DocumentsView.js';
import { ConversationsView } from '../components/ConversationsView.js';



// Alignment now lives in ONE place (the Align nav tab), not as a Vault tab — the
// old 'alignments' tab was a near-duplicate of Align that made the twin's stances
// feel like a second, repetitive Vault.
type VaultTab = 'memories' | 'documents' | 'conversations';

export function Vault() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<VaultTab>('memories');
  // Combinable filters: a set of active keys. Within a group (tiers / review
  // status / auto) selected keys are OR'd; the groups are AND'd together.
  const [filters, setFilters] = useState<Set<string>>(new Set());
  const toggleFilter = (k: string) => setFilters((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const [editing, setEditing] = useState<Memory | null>(null);
  // Multi-select for batch operations (delete / re-categorize).
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [delCode, setDelCode] = useState<{ code: string; input: string } | null>(null);
  const toggleSel = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  function exitSelect() { setSelectMode(false); setSelected(new Set()); setDelCode(null); }

  // Paginated: start at 1000 (covers months of auto-build); a banner offers to
  // load everything when the vault outgrows the current page.
  const [memLimit, setMemLimit] = useState(1000);
  const { data, isLoading, isError, refetch } = useQuery<{ memories: Memory[]; total?: number }>({
    queryKey: ['memory', 'list', memLimit],
    queryFn: () => api<{ memories: Memory[]; total?: number }>(`/api/memory?limit=${memLimit}`),
  });
  const totalMemories = data?.total ?? data?.memories.length ?? 0;
  const truncated = totalMemories > (data?.memories.length ?? 0);

  // Per-card thumbs: 👍 = reviewed/trusted, 👎 = send to Align to confirm/correct.
  const [alignMsg, setAlignMsg] = useState<string | null>(null);
  const flashMsg = (t: string, ms = 2500) => { setAlignMsg(t); window.setTimeout(() => setAlignMsg(null), ms); };
  const requestAlign = useMutation({
    mutationFn: (id: string) => api('/api/analysis/from-memory', { method: 'POST', body: JSON.stringify({ memoryId: id }) }),
    onSuccess: () => { flashMsg('👎 Envoyé vers Align ✓'); qc.invalidateQueries({ queryKey: ['analysis'] }); },
    onError: () => { flashMsg('Échec — mode API requis ?', 3000); },
  });
  const markReviewed = useMutation({
    mutationFn: (vars: { id: string; reviewed: boolean }) => api(`/api/memory/${vars.id}`, { method: 'PATCH', body: JSON.stringify({ reviewed: vars.reviewed }) }),
    onSuccess: (_r, vars) => { flashMsg(vars.reviewed ? '👍 Reviewed' : 'Review retiré'); qc.invalidateQueries({ queryKey: ['memory'] }); },
  });
  // Translate-on-demand (content stays in its original language). Cached server-
  // side by (text, lang), so repeats cost no tokens.
  const [prefs] = usePrefs();
  const t = useT();
  const [translated, setTranslated] = useState<Record<string, string>>({});
  const translateMem = useMutation({
    mutationFn: (vars: { id: string; text: string }) => api<{ text: string }>('/api/memory/translate', { method: 'POST', body: JSON.stringify({ text: vars.text, to: prefs.language }) }),
    onSuccess: (r, vars) => setTranslated((s) => ({ ...s, [vars.id]: r.text })),
    onError: () => flashMsg('Traduction indisponible (mode API requis)'),
  });

  // Duplicate detection + removal (DupGroup imported from DedupModal).
  const [dupGroups, setDupGroups] = useState<DupGroup[] | null>(null);
  const dedupScan = useMutation({
    mutationFn: () => api<{ groups: DupGroup[]; duplicates: number }>('/api/memory/dedup', { method: 'POST' }),
    onSuccess: (r) => { setDupGroups(r.groups); if (r.duplicates === 0) flashMsg('Aucun doublon trouvé 🎯'); },
  });
  const dedupApply = useMutation({
    mutationFn: (removeIds: string[]) => api<{ removed: number }>('/api/memory/dedup/apply', { method: 'POST', body: JSON.stringify({ removeIds }) }),
    onSuccess: (r) => { setDupGroups(null); flashMsg(`${r.removed} doublon(s) supprimé(s) ✓`); qc.invalidateQueries({ queryKey: ['memory'] }); },
  });

  const batch = useMutation({
    mutationFn: (vars: { action: 'delete' | 'visibility' | 'category' | 'review'; value?: string }) =>
      api<{ ok: boolean; count: number }>('/api/memory/batch', {
        method: 'POST',
        body: JSON.stringify({ ids: [...selected], action: vars.action, value: vars.value }),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['memory'] }); exitSelect(); },
  });

  // Count memories per visibility tier (for the filter chips).
  const visCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const m of data?.memories ?? []) c[m.visibility] = (c[m.visibility] ?? 0) + 1;
    return c;
  }, [data]);

  // Auto-built memory counts (for the filter chips).
  const autoCount = useMemo(() => (data?.memories ?? []).filter((m) => m.source === 'auto').length, [data]);
  const autoPending = useMemo(
    () => (data?.memories ?? []).filter((m) => m.source === 'auto' && m.reviewStatus === 'pending').length,
    [data],
  );

  const all = data?.memories ?? [];
  const reviewedCount = useMemo(() => all.filter((m) => m.reviewed).length, [all]);
  const notReviewedCount = useMemo(() => all.filter((m) => !m.reviewed).length, [all]);

  const filtered = useMemo(() => {
    const f = filters;
    const selTiers = (TIERS as readonly string[]).filter((t) => f.has(t));
    const rv = f.has('reviewed'), nrv = f.has('not_reviewed');
    const au = f.has('auto'), pe = f.has('pending');
    let memories = all.filter((m) => {
      if (selTiers.length && !selTiers.includes(m.visibility)) return false;           // tiers (OR)
      if ((rv || nrv) && !((rv && m.reviewed) || (nrv && !m.reviewed))) return false;   // review status (OR)
      if ((au || pe) && !((au && m.source === 'auto') || (pe && m.source === 'auto' && m.reviewStatus === 'pending'))) return false; // auto (OR)
      return true;
    });
    if (!q.trim()) return memories;
    const needle = q.toLowerCase();
    return memories.filter(
      (m) =>
        (m.title ?? '').toLowerCase().includes(needle) ||
        m.body.toLowerCase().includes(needle) ||
        m.themes.some((t) => t.toLowerCase().includes(needle)) ||
        m.people.some((t) => t.toLowerCase().includes(needle)),
    );
  }, [all, q, filters]);

  // ── Export (client-side, read-only) ──
  function downloadBlob(filename: string, content: string, type: string) {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  const stamp = () => new Date().toISOString().slice(0, 10);
  function exportJSON() {
    downloadBlob(`vault-${stamp()}.json`, JSON.stringify(data?.memories ?? [], null, 2), 'application/json');
  }
  function exportMarkdown() {
    const mems = [...(data?.memories ?? [])].sort((a, b) => (a.approxYear ?? 0) - (b.approxYear ?? 0));
    const lines: string[] = ['# Vault export', '', `${mems.length} memories — ${new Date().toLocaleDateString()}`, ''];
    for (const m of mems) {
      lines.push(`## ${m.title ?? 'Untitled'}`);
      const meta = [m.approxYear, m.lifeChapter?.replace(/_/g, ' ')].filter(Boolean).join(' · ');
      if (meta) lines.push(`*${meta}*`);
      lines.push('', m.body, '');
      const tags = [...m.people.map((p) => `@${p}`), ...m.themes].join(' ');
      if (tags) lines.push('`' + tags + '`', '');
    }
    downloadBlob(`vault-${stamp()}.md`, lines.join('\n'), 'text/markdown');
  }

  return (
    <div className="px-5 md:px-10 pb-12 max-w-7xl mx-auto space-y-8">
      <header className="flex items-end justify-between gap-6 pt-2">
        <div>
          <h1 className="font-serif text-4xl text-ink">{t('vault.title')} <em className="italic text-particle">{t('vault.titleEm')}</em></h1>
          <p className="text-[12px] text-ink/40 mt-2">
            {totalMemories} {t('vault.memories')}
            {truncated && (
              <button onClick={() => setMemLimit(Math.min(2000, totalMemories))}
                className="ml-2 px-2 py-0.5 rounded-full border border-amber/30 text-amber/80 hover:text-amber text-[9px] normal-case tracking-normal">
                {t('vault.loadAll', { shown: data?.memories.length ?? 0 })}
              </button>
            )}
          </p>
        </div>
        {tab === 'memories' && (
          <div className="flex items-center gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('vault.search')}
              className="w-44 sm:w-64 px-4 py-2.5 rounded-full border border-ink/15 bg-white/[0.02] font-serif text-sm text-ink focus:border-ink/40 focus:outline-none"
            />
            {(data?.memories.length ?? 0) > 0 && (
              <>
                {isOwner && (
                  <button onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
                    title="Select multiple memories for batch actions"
                    className={`px-3 py-2.5 rounded-full border text-[11px] ${selectMode ? 'bg-amber text-void border-amber' : 'border-ink/15 text-ink/55 hover:text-ink hover:border-ink/30'}`}>
                    {selectMode ? t('common.cancel') : t('vault.select')}
                  </button>
                )}
                {isOwner && (
                  <button onClick={() => dedupScan.mutate()} disabled={dedupScan.isPending}
                    title="Find & remove duplicate memories"
                    className="px-3 py-2.5 rounded-full border border-ink/15 text-[11px] text-ink/55 hover:text-ink hover:border-ink/30 disabled:opacity-40">
                    {dedupScan.isPending ? '…' : 'Dedup'}
                  </button>
                )}
                <button onClick={exportJSON} title="Export all memories as JSON"
                  className="px-3 py-2.5 rounded-full border border-ink/15 text-[11px] text-ink/55 hover:text-ink hover:border-ink/30">JSON</button>
                <button onClick={exportMarkdown} title="Export all memories as Markdown"
                  className="px-3 py-2.5 rounded-full border border-ink/15 text-[11px] text-ink/55 hover:text-ink hover:border-ink/30">MD</button>
              </>
            )}
          </div>
        )}
      </header>

      {/* Tab toggle */}
      <div className="flex items-center gap-2 border-b border-ink/[0.07] pb-2">
        {(isOwner ? (['memories', 'documents', 'conversations'] as VaultTab[]) : (['memories', 'documents'] as VaultTab[])).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-[12px] transition-colors ${
              tab === t ? 'bg-ink text-void border border-ink' : 'border border-ink/15 text-ink/55 hover:text-ink hover:border-ink/30'
            }`}>
            {t}
          </button>
        ))}
      </div>

      {/* Combinable filters — toggle any chips; they stack (AND across groups). */}
      {tab === 'memories' && (data?.memories.length ?? 0) > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap -mt-3">
          <button onClick={() => setFilters(new Set())}
            className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
              filters.size === 0 ? 'bg-ink text-void border-ink' : 'border-ink/15 text-ink/55 hover:text-ink'
            }`}>
            {t('vault.all')} {all.length}
          </button>
          {TIERS.map((t) => (
            <button key={t} onClick={() => toggleFilter(t)} disabled={!visCounts[t]}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                filters.has(t) ? `${TIER_COLOR[t]} bg-white/[0.05]` : 'border-ink/15 text-ink/45 hover:text-ink'
              } ${!visCounts[t] ? 'opacity-30 cursor-not-allowed' : ''}`}>
              {t.replace('_', ' ')} {visCounts[t] ?? 0}
            </button>
          ))}
          <span className="w-px h-4 bg-ink/10 mx-0.5" />
          <button onClick={() => toggleFilter('reviewed')}
            className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
              filters.has('reviewed') ? 'text-emerald-300 border-emerald-300/50 bg-white/[0.05]' : 'border-emerald-300/30 text-emerald-300/70 hover:text-emerald-300'
            }`}>
            {t('vault.reviewed')} {reviewedCount}
          </button>
          <button onClick={() => toggleFilter('not_reviewed')}
            className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
              filters.has('not_reviewed') ? 'text-amber border-amber/50 bg-white/[0.05]' : 'border-amber/30 text-amber/70 hover:text-amber'
            }`}>
            {t('vault.notReviewed')} {notReviewedCount}
          </button>
          {autoCount > 0 && (
            <>
              <span className="w-px h-4 bg-ink/10 mx-0.5" />
              <button onClick={() => toggleFilter('auto')}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                  filters.has('auto') ? 'text-particle border-particle/50 bg-white/[0.05]' : 'border-particle/30 text-particle/70 hover:text-particle'
                }`}>
                ✨ auto {autoCount}
              </button>
              {autoPending > 0 && (
                <button onClick={() => toggleFilter('pending')}
                  className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                    filters.has('pending') ? 'text-particle border-particle/50 bg-white/[0.05]' : 'border-particle/30 text-particle/70 hover:text-particle'
                  }`}>
                  review {autoPending}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'conversations' ? (
        <ConversationsView />
      ) : tab === 'documents' ? (
        <DocumentsView />
      ) : isLoading ? (
        <div className="text-ink/40 text-[12px]">Loading…</div>
      ) : isError ? (
        <div className="text-center py-16 space-y-3">
          <p className="font-serif italic text-ink/50">{t('vault.unreachable')}</p>
          <button onClick={() => refetch()} className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink">{t('common.retry')}</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 font-serif italic text-ink/40">
          {q || filters.size > 0 ? t('vault.emptyFilter') : t('vault.empty')}
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 items-start">
          {filtered.map((m) => (
            <MemoryCard key={m.id} m={m} isOwner={isOwner} selectMode={selectMode}
              selected={selected.has(m.id)} translatedText={translated[m.id]}
              busy={{ translate: translateMem.isPending, review: markReviewed.isPending, align: requestAlign.isPending }}
              onOpen={() => setEditing(m)}
              onToggleSelect={() => toggleSel(m.id)}
              onTranslate={() => translateMem.mutate({ id: m.id, text: m.body })}
              onShowOriginal={() => setTranslated((st) => { const n = { ...st }; delete n[m.id]; return n; })}
              onThumbUp={() => markReviewed.mutate({ id: m.id, reviewed: !m.reviewed })}
              onThumbDown={() => requestAlign.mutate(m.id)}
            />
          ))}
        </ul>
      )}

      {/* Batch action bar — appears in select mode (memories tab). */}
      {tab === 'memories' && selectMode && (
        <div className="sticky bottom-4 z-40 mx-auto max-w-3xl flex items-center gap-2 flex-wrap rounded-2xl border border-amber/30 bg-void/95 backdrop-blur px-4 py-3 shadow-xl">
          <span className="text-[11.5px] text-ink/70">{t('vault.selected', { n: selected.size })}</span>
          <button onClick={() => setSelected(new Set(filtered.map((m) => m.id)))}
            className="px-2.5 py-1 rounded-full border border-ink/15 text-[11px] text-ink/55 hover:text-ink">{t('vault.selectAll', { n: filtered.length })}</button>
          {selected.size > 0 && <button onClick={() => setSelected(new Set())}
            className="px-2.5 py-1 rounded-full border border-ink/15 text-[11px] text-ink/55 hover:text-ink">{t('vault.clear')}</button>}
          <div className="flex-1" />
          {selected.size > 0 && (
            <>
              <button disabled={batch.isPending} onClick={() => batch.mutate({ action: 'review' })}
                title="Mark as reviewed/trusted — reinforces the twin and disables alignment for these"
                className="px-3 py-1.5 rounded-full bg-emerald-300/90 text-void text-[11px] hover:bg-emerald-300 disabled:opacity-40">
                ✓ Reviewed
              </button>
              <select disabled={batch.isPending} defaultValue=""
                onChange={(e) => { if (e.target.value) { batch.mutate({ action: 'visibility', value: e.target.value }); e.target.value = ''; } }}
                title="Change visibility tier for the selected memories"
                className="px-2.5 py-1.5 rounded-full border border-ink/20 bg-void text-[11px] text-ink/70">
                <option value="">{t('vault.setVisibility')}</option>
                {TIERS.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
              <select disabled={batch.isPending} defaultValue=""
                onChange={(e) => { if (e.target.value) { batch.mutate({ action: 'category', value: e.target.value }); e.target.value = ''; } }}
                title="Change category for the selected memories"
                className="px-2.5 py-1.5 rounded-full border border-ink/20 bg-void text-[11px] text-ink/70">
                <option value="">{t('vault.setCategory')}</option>
                <option value="personal">personal</option>
                <option value="professional">professional</option>
              </select>
              <button disabled={batch.isPending}
                onClick={() => setDelCode({ code: String(Math.floor(1000 + Math.random() * 9000)), input: '' })}
                className="px-3 py-1.5 rounded-full bg-red-400 text-void text-[11px] hover:bg-red-300 disabled:opacity-40">
                {t('vault.deleteN', { n: selected.size })}
              </button>
            </>
          )}
        </div>
      )}

      {/* 4-digit code confirmation for batch delete */}
      {delCode && (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-black/60 p-4" onClick={() => setDelCode(null)}>
          <div className="w-full max-w-xs rounded-2xl border border-ink/15 bg-void p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="font-serif text-[16px] text-ink">Delete {selected.size} memor{selected.size > 1 ? 'ies' : 'y'}?</div>
            <p className="font-serif italic text-[13px] text-ink/55">This is permanent. Type the code below to confirm.</p>
            <div className="text-center font-mono text-2xl tracking-[0.4em] text-amber select-all py-1">{delCode.code}</div>
            <input value={delCode.input} autoFocus inputMode="numeric" placeholder="4-digit code"
              onChange={(e) => setDelCode((d) => d && ({ ...d, input: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
              onKeyDown={(e) => { if (e.key === 'Enter' && delCode.input === delCode.code) batch.mutate({ action: 'delete' }); }}
              className="w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-center text-lg tracking-[0.3em] text-ink focus:border-ink/40 focus:outline-none" />
            <div className="flex items-center gap-2 justify-end pt-1">
              <button onClick={() => setDelCode(null)} className="px-3 py-1.5 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink">Cancel</button>
              <button onClick={() => batch.mutate({ action: 'delete' })} disabled={delCode.input !== delCode.code || batch.isPending}
                className="px-4 py-1.5 rounded-full bg-red-400 text-void text-[11.5px] hover:bg-red-300 disabled:opacity-30">Delete</button>
            </div>
          </div>
        </div>
      )}

      {alignMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full border border-amber/40 bg-void/95 backdrop-blur text-[11.5px] text-amber shadow-xl">
          {alignMsg}
        </div>
      )}

      {dupGroups && dupGroups.length > 0 && (
        <DedupModal groups={dupGroups} applying={dedupApply.isPending}
          onClose={() => setDupGroups(null)} onApply={(ids) => dedupApply.mutate(ids)} />
      )}

      {editing && (
        <MemoryEditor
          memory={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['memory'] }); }}
        />
      )}
    </div>
  );
}
